import { 
  SocialMediaPost, 
  AnalyticsResponse,
  SponsorAnalytics
} from '../types/socialMedia';
import { apiService } from './api';

// Customer Account Types
export interface CustomerAccount {
  id: string;
  platformId: number;
  platformName: string;
  platformDisplayName: string;
  categoryId: number;
  categoryName: string;
  username: string;
  displayName: string;
  followerCount: number;
  verified: boolean;
  isActive: boolean;
}

export interface CustomerAccountsResponse {
  success: boolean;
  data?: {
    customer: {
      id: string;
      customerName: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
      platformAccounts: CustomerAccount[];
      publications: any[];
      totalPlatformAccounts: number;
      totalPublications: number;
    };
  };
  message?: string;
  error?: string;
}

// DEPRECATED: Social Sponsors API Types - Backend routes removed, now using activation-based sponsorship detection
/** @deprecated Backend routes removed - use activation-based sponsorship detection instead */
export interface SocialSponsor {
  sponsorId: number;
  customerId: string;
  sponsorName: string;
  sponsorDisplayName: string | null;
  sponsorLogoUrl: string | null;
  sponsorWebsiteUrl: string | null;
  sponsorContactEmail: string | null;
  sponsorDescription: string | null;
  sponsorType: 'brand' | 'agency' | 'individual' | 'nonprofit' | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  customer: {
    id: string;
    customerName: string;
  };
  analytics: {
    totalPosts: number;
    totalEmv: string;
    avgEmv: string;
    totalEngagement: number;
    metrics: {
      totalLikes: number;
      totalComments: number;
      totalShares: number;
      totalViews: number;
    };
  };
}

export interface CreateSocialSponsorRequest {
  customerId: string;
  sponsorName: string;
  sponsorDisplayName?: string;
  sponsorLogoUrl?: string;
  sponsorWebsiteUrl?: string;
  sponsorContactEmail?: string;
  sponsorDescription?: string;
  sponsorType?: 'brand' | 'agency' | 'individual' | 'nonprofit';
  isActive?: boolean;
}

export interface UpdateSocialSponsorRequest {
  sponsorName?: string;
  sponsorDisplayName?: string;
  sponsorLogoUrl?: string;
  sponsorWebsiteUrl?: string;
  sponsorContactEmail?: string;
  sponsorDescription?: string;
  sponsorType?: 'brand' | 'agency' | 'individual' | 'nonprofit';
  isActive?: boolean;
}

export interface SocialSponsorsResponse {
  success: boolean;
  message: string;
  data: {
    sponsors: SocialSponsor[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalSponsors: number;
      sponsorsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    filters: {
      customerId: string;
      isActive?: string;
      sponsorType?: string;
    };
  };
}

export interface SocialSponsorResponse {
  success: boolean;
  message: string;
  data: {
    sponsor: SocialSponsor;
  };
}

export interface GetSocialSponsorsParams {
  customerId: string;
  isActive?: 'true' | 'false';
  sponsorType?: 'brand' | 'agency' | 'individual' | 'nonprofit';
  period?: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom';
  page?: string;
  limit?: string;
}

// Lightweight sponsor list item (for dropdown selectors)
export interface SocialSponsorListItem {
  sponsorId: number;
  sponsorName: string;
  sponsorDisplayName: string | null;
  sponsorType: 'brand' | 'agency' | 'individual' | 'nonprofit' | null;
}

export interface SocialSponsorsListResponse {
  success: boolean;
  message: string;
  data: {
    sponsors: SocialSponsorListItem[];
    totalSponsors: number;
  };
}

// Social Team Members API Types
export interface SocialTeamMember {
  teamMemberId: number;
  customerId: string;
  teamMemberName: string;
  teamMemberEmail: string;
  teamMemberPhone: string | null;
  department: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  customer: {
    id: string;
    customerName: string;
  };
}

export interface CreateSocialTeamMemberRequest {
  customerId: string;
  teamMemberName: string;
  teamMemberEmail: string;
  teamMemberPhone?: string;
  department?: string;
  isActive?: boolean;
}

export interface UpdateSocialTeamMemberRequest {
  teamMemberName?: string;
  teamMemberEmail?: string;
  teamMemberPhone?: string;
  department?: string;
  isActive?: boolean;
}

export interface SocialTeamMembersResponse {
  success: boolean;
  message: string;
  data: {
    teamMembers: SocialTeamMember[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalTeamMembers: number;
      teamMembersPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    filters: {
      customerId: string;
      isActive?: string;
      department?: string;
    };
  };
}

export interface SocialTeamMemberResponse {
  success: boolean;
  message: string;
  data: {
    teamMember: SocialTeamMember;
  };
}

export interface GetSocialTeamMembersParams {
  customerId: string;
  isActive?: 'true' | 'false' | 'all';
  department?: string;
  page?: string;
  limit?: string;
}

// Social Sponsor Team API Types
export interface SocialSponsorTeam {
  sponsorTeamId: number;
  sponsorId: number;
  teamMemberId: number;
  teamMemberRole: string;
  isPrimaryContact: boolean;
  accessLevel: 'read' | 'write' | 'admin';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  sponsor: {
    sponsorId: number;
    sponsorName: string;
    sponsorDisplayName: string | null;
    customerId: string;
  };
  teamMember: {
    teamMemberId: number;
    teamMemberName: string;
    teamMemberEmail: string;
    customerId: string;
  };
}

export interface CreateSocialSponsorTeamRequest {
  sponsorId: number;
  teamMemberId: number;
  teamMemberRole: string;
  isPrimaryContact?: boolean;
  accessLevel?: 'read' | 'write' | 'admin';
  isActive?: boolean;
}

export interface UpdateSocialSponsorTeamRequest {
  teamMemberRole?: string;
  isPrimaryContact?: boolean;
  accessLevel?: 'read' | 'write' | 'admin';
  isActive?: boolean;
}

export interface SocialSponsorTeamResponse {
  success: boolean;
  message: string;
  data: {
    sponsorTeam: SocialSponsorTeam[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalAssignments: number;
      assignmentsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    filters: {
      sponsorId?: string;
      teamMemberId?: string;
      customerId?: string;
      isActive?: string;
      isPrimaryContact?: string;
      teamMemberRole?: string;
    };
  };
}

export interface SocialSponsorTeamSingleResponse {
  success: boolean;
  message: string;
  data: {
    sponsorTeam: SocialSponsorTeam;
  };
}

export interface GetSocialSponsorTeamParams {
  sponsorId?: string;
  teamMemberId?: string;
  customerId?: string;
  isActive?: 'true' | 'false';
  isPrimaryContact?: 'true' | 'false';
  teamMemberRole?: string;
  page?: string;
  limit?: string;
}

// Social Post Sponsors API Types
export interface PostSponsor {
  postSponsorId: number;
  postId: number;
  sponsorId: number;
  sponsorshipType: 'paid' | 'gifted' | 'partnership' | 'affiliate';
  sponsorshipAmount: string | null;
  currency: string;
  disclosureText: string | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  performanceBonusCriteria: object | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  sponsor: {
    sponsorId: number;
    sponsorName: string;
    sponsorDisplayName: string | null;
    sponsorType: string | null;
    sponsorLogoUrl: string | null;
    sponsorWebsiteUrl: string | null;
  };
  post?: {
    postId: number;
    caption: string | null;
    postUrl: string;
    postType: string;
    contentType: string;
    postedAt: string;
    account: {
      username: string;
      displayName: string | null;
    };
    analytics: {
      totalEmv: string;
      totalEngagement: number;
      metrics: {
        likesCount: number;
        commentsCount: number;
        sharesCount: number;
        viewsCount: number;
      };
    };
  };
}

export interface PostSponsorsResponse {
  success: boolean;
  message: string;
  data: {
    post: {
      postId: number;
      caption: string | null;
      postUrl: string;
    };
    sponsors: PostSponsor[];
    totalSponsors: number;
  };
}

export interface SponsorAssociation {
  sponsorId: number;
  sponsorshipType?: 'paid' | 'gifted' | 'partnership' | 'affiliate';
  sponsorshipAmount?: number;
  currency?: string;
  disclosureText?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  performanceBonusCriteria?: object;
  isActive?: boolean;
}

export interface AssociateSponsorsRequest {
  postId: number;
  sponsorAssociations: SponsorAssociation[];
}

export interface AssociateSponsorsResponse {
  success: boolean;
  message: string;
  data: {
    postId: number;
    newAssociations: PostSponsor[];
    skippedDuplicates: number;
  };
}

export interface UpdatePostSponsorRequest {
  sponsorshipType?: 'paid' | 'gifted' | 'partnership' | 'affiliate';
  sponsorshipAmount?: number;
  currency?: string;
  disclosureText?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  performanceBonusCriteria?: object;
  isActive?: boolean;
}

export interface DisassociateSponsorsRequest {
  postId: number;
  sponsorIds: number[];
}

export interface DisassociateSponsorsResponse {
  success: boolean;
  message: string;
  data: {
    postId: number;
    removedSponsors: string[];
    removedCount: number;
  };
}

export interface SponsorPostsResponse {
  success: boolean;
  message: string;
  data: {
    sponsor: {
      sponsorId: number;
      sponsorName: string;
      sponsorDisplayName: string | null;
    };
    sponsoredPosts: PostSponsor[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalPosts: number;
      postsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    filters: {
      sponsorId: number;
      isActive?: string;
      sponsorshipType?: string;
    };
  };
}

export interface GetSponsorPostsParams {
  isActive?: 'true' | 'false';
  sponsorshipType?: 'paid' | 'gifted' | 'partnership' | 'affiliate';
  period?: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom';
  customStartDate?: string;
  customEndDate?: string;
  page?: string;
  limit?: string;
}

// New types for Social Dashboard API
export interface DashboardAnalyticsParams {
  customerId: string;
  period?: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom';
  platformIds?: number | number[];
  accountIds?: string | string[];
  postTypes?: string | string[];
  customStartDate?: string;
  customEndDate?: string;
  excludeTracked?: boolean;
  categoryId?: number;
}

export interface DashboardAnalyticsResponse {
  success: boolean;
  message: string;
  data: {
    customer: {
      customerId: string;
      customerName: string;
    };
    period: {
      type: string;
      current: {
        start: string;
        end: string;
      };
      previous: {
        start: string;
        end: string;
      };
    };
    filters: {
      platforms: Array<{
        id: number;
        platformName: string;
        platformDisplayName: string;
      }>;
      accounts: Array<{
        id: string;
        username: string;
        displayName: string;
        platform: {
          id: number;
          platformName: string;
          platformDisplayName: string;
        };
      }>;
    };
    metrics: {
      totalPosts: {
        current: number;
        previous: number;
        change: string;
      };
      totalEmv: {
        current: string;
        previous: string;
        change: string;
      };
      averageEmv: {
        current: string;
        previous: string;
        change: string;
      };
      totalViews: {
        current: number;
        previous: number;
        change: string;
      };
      totalLikes: {
        current: number;
        previous: number;
        change: string;
      };
      totalComments: {
        current: number;
        previous: number;
        change: string;
      };
      totalShares: {
        current: number;
        previous: number;
        change: string;
      };
      sponsoredEmv: {
        current: string;
        change: string;
      };
      adEmv: {
        current: string;
        change: string;
      };
    };
    radarChart?: {
      chart_type: string;
      dimensions: Array<{
        axis: string;
        value: number;
        max_value: number;
        description: string;
      }>;
      overall_score: number;
      narrative: string;
    };
  };
}

export interface SocialPlatform {
  id: number;
  name: string;
  displayName: string;
  accountCount: number;
}

export interface SocialProfile {
  id: string;
  username: string;
  displayName: string;
  profilePicUrl: string | null;
  followerCount: number;
  verified: boolean;
  categoryId?: number;
  categoryName?: string | null;
  platform: {
    id: number;
    name: string;
    displayName: string;
  };
  // EMV rates (new backend fields)
  emvViewRate?: number;
  emvLikeRate?: number;
  emvCommentRate?: number;
  emvShareRate?: number;
}

export interface PostType {
  id: number;
  typeName: string;
  displayName: string;
  postCount?: number;
  platform: {
    id: number;
    name: string;
    displayName: string;
  };
}

export interface PostTypesResponse {
  success: boolean;
  message: string;
  data: {
    postTypes: PostType[];
    filters: {
      customerId: string | null;
      platformId: number | null;
    };
  };
}

export interface TopPostsParams {
  customerId: string;
  period?: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom';
  platformIds?: number | number[];
  accountIds?: string | string[];
  postTypes?: string | string[];
  customStartDate?: string;
  customEndDate?: string;
  page?: number;
  limit?: number;
  sortBy?: 'emv' | 'likes' | 'views' | 'comments' | 'date';
  sortOrder?: 'asc' | 'desc';
  searchTerm?: string;
  isSponsored?: boolean;
  isAd?: boolean;
  hasCollaborators?: boolean;
  categoryId?: number;
}

export interface TopPost {
  id: number;
  partitionKey: number;
  platformPostId: string;
  caption: string;
  postUrl: string;
  altText: string | null;
  postedAt: string;
  isSponsored: boolean;
  isAd: boolean;
  postType: string;
  contentType: string;
  // Brand Scout content category — set only for scout-tracked accounts, else null.
  contentCategory?: string | null;
  collaboratorUsername?: string | null;
  thumbnailBlobPath?: string | null;
  platform: {
    id: number;
    name: string;
    displayName: string;
  };
  account: {
    id: string;
    username: string;
    displayName: string;
    profilePicUrl: string | null;
    verified: boolean;
  };
  metrics: {
    likes: number;
    comments: number;
    shares: number;
    views: number;
    emv: string;
    hasOverrides?: boolean;
    overrideMetadata?: Record<string, boolean> | null;
    // Platform-native metrics not in dedicated columns (e.g. Instagram Story
    // reach/replies/navigation breakdown/link_clicks). Null for posts without them.
    extendedMetrics?: Record<string, any> | null;
  };
}

export interface TopPostsResponse {
  success: boolean;
  message: string;
  data: {
    customer: {
      customerId: string;
      customerName: string;
    };
    period: {
      type: string;
      start: string;
      end: string;
    };
    posts: TopPost[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalPosts: number;
      postsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    filters: {
      sortBy: string;
      platformIds: number[] | null;
      accountIds: string[] | null;
    };
  };
}

class SocialMediaService {
  private baseUrl = '/social-media';
  private dashboardBaseUrl = '/social/dashboard';

  getThumbnailUrl(postId: number, customerId: string): string {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
    return `${baseUrl}/social/dashboard/posts/${postId}/thumbnail?customerId=${customerId}`;
  }

  // Customer Accounts Management
  async getCustomerAccounts(customerId: string, categoryIds?: string, isActive?: boolean): Promise<CustomerAccountsResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (categoryIds) {
        queryParams.append('categoryIds', categoryIds);
      }
      
      if (isActive !== undefined) {
        queryParams.append('isActive', isActive.toString());
      }
      
      const url = queryParams.toString() 
        ? `/customers/${customerId}?${queryParams.toString()}`
        : `/customers/${customerId}`;
      
      const response = await apiService.get<CustomerAccountsResponse>(url);
      return response;
    } catch (error: any) {
      if (error?.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: error?.message || 'Failed to fetch customer accounts',
        error: 'NETWORK_ERROR'
      };
    }
  }


  // Analytics
  async getAnalytics(params?: {
    platform?: 'instagram' | 'tiktok';
    sponsor?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<AnalyticsResponse> {
    return await apiService.get<AnalyticsResponse>(`${this.baseUrl}/analytics`, params);
  }

  async getSponsorAnalytics(sponsor?: string): Promise<SponsorAnalytics[]> {
    const response = await apiService.get<{ sponsors: SponsorAnalytics[] }>(`${this.baseUrl}/analytics/sponsors`, { sponsor });
    return response.sponsors;
  }

  // Sponsorship Management
  async addSponsorship(postId: string, sponsor: string, tag: string): Promise<SocialMediaPost> {
    const response = await apiService.post<{ post: SocialMediaPost }>(`${this.baseUrl}/posts/${postId}/sponsorship`, {
      sponsor,
      sponsorshipTag: tag
    });
    return response.post;
  }

  async removeSponsorship(postId: string): Promise<SocialMediaPost> {
    const response = await apiService.delete<{ post: SocialMediaPost }>(`${this.baseUrl}/posts/${postId}/sponsorship`);
    return response.post;
  }

  async getSponsors(): Promise<string[]> {
    const response = await apiService.get<{ sponsors: string[] }>(`${this.baseUrl}/sponsors`);
    return response.sponsors;
  }

  // EMV Calculations
  async recalculateEMV(postIds?: string[]): Promise<SocialMediaPost[]> {
    const response = await apiService.post<{ posts: SocialMediaPost[] }>(`${this.baseUrl}/emv/recalculate`, { postIds });
    return response.posts;
  }

  async updateEMVRates(rates: {
    viewRate?: number;
    commentRate?: number;
    likeRate?: number;
    shareRate?: number;
  }): Promise<void> {
    await apiService.put(`${this.baseUrl}/emv/rates`, rates);
  }

  async getEMVRates(): Promise<{
    viewRate: number;
    commentRate: number;
    likeRate: number;
    shareRate: number;
  }> {
    return await apiService.get(`${this.baseUrl}/emv/rates`);
  }

  // Data Export
  async exportData(params?: {
    platform?: 'instagram' | 'tiktok';
    sponsor?: string;
    format?: 'csv' | 'json' | 'xlsx';
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Blob> {
    return await apiService.getBlob(`${this.baseUrl}/export`, params);
  }

  // File Upload Tracking
  async getUploadHistory(): Promise<{
    uploads: Array<{
      id: string;
      filename: string;
      platform: 'instagram' | 'tiktok';
      uploadDate: string;
      postsCount: number;
      totalEMV: number;
      status: 'completed' | 'processing' | 'failed';
    }>;
  }> {
    return await apiService.get(`${this.baseUrl}/uploads`);
  }

  async deleteUpload(uploadId: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/uploads/${uploadId}`);
  }

  // Search and Filtering
  async searchPosts(query: string, filters?: {
    platform?: 'instagram' | 'tiktok';
    sponsor?: string;
    minEMV?: number;
    maxEMV?: number;
  }): Promise<SocialMediaPost[]> {
    const response = await apiService.get<{ posts: SocialMediaPost[] }>(`${this.baseUrl}/posts/search`, {
      q: query,
      ...filters
    });
    return response.posts;
  }

  // Performance Metrics

  async getTrendingHashtags(platform?: 'instagram' | 'tiktok'): Promise<Array<{
    hashtag: string;
    count: number;
    totalEMV: number;
  }>> {
    const response = await apiService.get<{ hashtags: Array<{
      hashtag: string;
      count: number;
      totalEMV: number;
    }> }>(`${this.baseUrl}/hashtags/trending`, { platform });
    return response.hashtags;
  }

  // Team Member Tracking
  async getTeamMembers(): Promise<Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    assignedSponsors: string[];
  }>> {
    const response = await apiService.get<{ 
      members: Array<{
        id: string;
        name: string;
        email: string;
        role: string;
        assignedSponsors: string[];
      }> 
    }>(`${this.baseUrl}/team`);
    return response.members;
  }

  async assignSponsorToMember(memberId: string, sponsor: string): Promise<void> {
    await apiService.post(`${this.baseUrl}/team/${memberId}/sponsors`, { sponsor });
  }

  async unassignSponsorFromMember(memberId: string, sponsor: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/team/${memberId}/sponsors/${sponsor}`);
  }

  // Bulk Operations
  async bulkUpdatePosts(updates: Array<{
    id: string;
    updates: Partial<SocialMediaPost>;
  }>): Promise<SocialMediaPost[]> {
    const response = await apiService.put<{ posts: SocialMediaPost[] }>(`${this.baseUrl}/posts/bulk`, { updates });
    return response.posts;
  }

  async bulkDeletePosts(postIds: string[]): Promise<void> {
    await apiService.delete(`${this.baseUrl}/posts/bulk`, { postIds });
  }

  async bulkAddSponsorship(postIds: string[], sponsor: string, tag: string): Promise<SocialMediaPost[]> {
    const response = await apiService.post<{ posts: SocialMediaPost[] }>(`${this.baseUrl}/posts/bulk/sponsorship`, {
      postIds,
      sponsor,
      sponsorshipTag: tag
    });
    return response.posts;
  }

  // Social Dashboard API Methods
  async getDashboardAnalytics(params: DashboardAnalyticsParams): Promise<DashboardAnalyticsResponse> {
    const { customerId, ...queryParams } = params;
    
    // Handle array parameters properly
    const processedParams: any = { customerId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        // For multiple values, append each separately
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        // For multiple values, append each separately  
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }
    if (params.postTypes) {
      if (Array.isArray(params.postTypes)) {
        // For multiple values, append each separately  
        delete processedParams.postTypes;
      } else {
        processedParams.postTypes = params.postTypes;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/analytics`;
    const searchParams = new URLSearchParams();
    
    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }
    if (params.postTypes && Array.isArray(params.postTypes)) {
      params.postTypes.forEach(type => searchParams.append('postTypes', type));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get<DashboardAnalyticsResponse>(endpoint);
  }

  async getSocialPlatforms(customerId: string): Promise<{ success: boolean; message: string; data: { platforms: SocialPlatform[] } }> {
    return await apiService.get(`${this.dashboardBaseUrl}/platforms`, { customerId });
  }

  async getSocialProfiles(customerId: string, platformId?: number): Promise<{ success: boolean; message: string; data: { profiles: SocialProfile[] } }> {
    const params: any = { customerId };
    if (platformId) {
      params.platformId = platformId;
    }
    return await apiService.get(`${this.dashboardBaseUrl}/profiles`, params);
  }

  async getPostTypes(customerId?: string, platformId?: number): Promise<PostTypesResponse> {
    const params: any = {};
    if (customerId) {
      params.customerId = customerId;
    }
    if (platformId) {
      params.platformId = platformId;
    }
    return await apiService.get(`${this.dashboardBaseUrl}/post-types`, params);
  }

  // Customer Management
  async getCustomers(): Promise<{ success: boolean; message: string; data: { customers: Array<{ id: string; customerName: string; isActive: boolean; totalPlatformAccounts: number; totalPublications: number; }>; totalCustomers: number; } }> {
    return await apiService.get('/customers');
  }

  // Get brand analytics data
  async getBrandAnalytics(params: {
    customerId: string;
    period?: string;
    page?: number;
    limit?: number;
    customStartDate?: string;
    customEndDate?: string;
    platformIds?: string[];
    accountIds?: string[];
    postTypes?: string[];
    search?: string;
    sortBy?: 'brandName' | 'postEmv' | 'confidence' | 'detectionCount' | 'totalDurationSeconds' | 'postCreateDate';
  }): Promise<{
    success: boolean;
    data: {
      brands: Array<{
        brandName: string;
        postCount: number;
        totalDurationSeconds: number;
        totalDetections: number;
        totalEmv: string;
        avgConfidence: number;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
      filters: any;
    };
    message?: string;
  }> {
    try {
      const { customerId, ...queryParams } = params;
      
      // Handle array parameters properly
      const processedParams: any = { customerId, ...queryParams };
      if (params.platformIds) {
        if (Array.isArray(params.platformIds)) {
          delete processedParams.platformIds;
        } else {
          processedParams.platformIds = params.platformIds;
        }
      }
      if (params.accountIds) {
        if (Array.isArray(params.accountIds)) {
          delete processedParams.accountIds;
        } else {
          processedParams.accountIds = params.accountIds;
        }
      }
      if (params.postTypes) {
        if (Array.isArray(params.postTypes)) {
          delete processedParams.postTypes;
        } else {
          processedParams.postTypes = params.postTypes;
        }
      }

      let endpoint = `${this.dashboardBaseUrl}/post-brands`;
      const searchParams = new URLSearchParams();
      
      Object.entries(processedParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });

      // Handle array parameters by appending multiple times
      if (params.platformIds && Array.isArray(params.platformIds)) {
        params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
      }
      if (params.accountIds && Array.isArray(params.accountIds)) {
        params.accountIds.forEach(id => searchParams.append('accountIds', id));
      }
      if (params.postTypes && Array.isArray(params.postTypes)) {
        params.postTypes.forEach(type => searchParams.append('postTypes', type));
      }

      if (searchParams.toString()) {
        endpoint += `?${searchParams.toString()}`;
      }

      const response = await apiService.get(endpoint);
      return response as any;
    } catch (error) {
      console.error('Error fetching brand analytics:', error);
      throw error;
    }
  }

  async getTopPostsByPlatform(params: DashboardAnalyticsParams & { limitPerPlatform?: number }): Promise<{
    success: boolean;
    message: string;
    data: {
      customer: {
        customerId: string;
        customerName: string;
      };
      period: {
        type: string;
        start: string;
        end: string;
      };
      postsByPlatform: {
        [platformName: string]: Array<{
          id: number;
          platformPostId: string;
          caption: string;
          postUrl: string;
          altText: string | null;
          postedAt: string;
          isSponsored: boolean;
          isAd: boolean;
          postType: string;
          contentType: string;
          collaboratorUsername: string | null;
          account: {
            id: string;
            username: string;
            displayName: string;
            profilePicUrl: string | null;
            verified: boolean;
          };
          metrics: {
            likes: number;
            comments: number;
            shares: number;
            views: number;
            emv: string;
          };
        }>;
      };
      platformInfo: {
        [platformName: string]: {
          id: number;
          name: string;
          displayName: string;
        };
      };
      summary: {
        totalPosts: number;
        platformCount: number;
        limitPerPlatform: number;
      };
      filters: {
        sortBy: string;
        platformIds: number[] | null;
        accountIds: string[] | null;
      };
    };
  }> {
    const { customerId, ...queryParams } = params;
    
    // Handle array parameters properly
    const processedParams: any = { customerId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/top-posts-by-platform`;
    const searchParams = new URLSearchParams();
    
    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get(endpoint, undefined, 30000);
  }

  async getTopPosts(params: TopPostsParams): Promise<TopPostsResponse> {
    const { customerId, ...queryParams } = params;
    
    // Handle array parameters properly
    const processedParams: any = { customerId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }
    if (params.postTypes) {
      if (Array.isArray(params.postTypes)) {
        delete processedParams.postTypes;
      } else {
        processedParams.postTypes = params.postTypes;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/top-posts`;
    const searchParams = new URLSearchParams();
    
    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }
    if (params.postTypes && Array.isArray(params.postTypes)) {
      params.postTypes.forEach(type => searchParams.append('postTypes', type));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get<TopPostsResponse>(endpoint);
  }

  async getHashtagAnalytics(params: DashboardAnalyticsParams & { limit?: number }): Promise<{
    success: boolean;
    message: string;
    data: {
      customer: {
        customerId: string;
        customerName: string;
      };
      period: {
        type: string;
        current: {
          start: string;
          end: string;
        };
        previous: {
          start: string;
          end: string;
        };
      };
      filters: {
        platforms: Array<{
          id: number;
          platformName: string;
          platformDisplayName: string;
        }>;
        accounts: Array<{
          id: string;
          username: string;
          displayName: string;
          platform: {
            id: number;
            platformName: string;
            platformDisplayName: string;
          };
        }>;
        limit: number;
      };
      summary: {
        totalHashtags: number;
        topHashtagsReturned: number;
      };
      hashtags: Array<{
        hashtag: string;
        current: {
          postCount: number;
          totalViews: number;
          totalLikes: number;
          totalComments: number;
          totalShares: number;
          totalEmv: string;
          avgViewsPerPost: number;
          avgLikesPerPost: number;
          avgCommentsPerPost: number;
          avgSharesPerPost: number;
          avgEmvPerPost: string;
        };
        previous: {
          postCount: number;
          totalViews: number;
          totalLikes: number;
          totalComments: number;
          totalShares: number;
          totalEmv: string;
          avgViewsPerPost: number;
          avgLikesPerPost: number;
          avgCommentsPerPost: number;
          avgSharesPerPost: number;
          avgEmvPerPost: string;
        };
        changes: {
          postCount: string;
          totalViews: string;
          totalLikes: string;
          totalComments: string;
          totalShares: string;
          totalEmv: string;
          avgViewsPerPost: string;
          avgLikesPerPost: string;
          avgCommentsPerPost: string;
          avgSharesPerPost: string;
          avgEmvPerPost: string;
        };
      }>;
    };
  }> {
    const { customerId, ...queryParams } = params;
    
    // Handle array parameters properly
    const processedParams: any = { customerId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/hashtag-analytics`;
    const searchParams = new URLSearchParams();
    
    // Add non-array parameters
    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get(endpoint, undefined, 30000); // 30 second timeout
  }

  async getHashtagPosts(params: DashboardAnalyticsParams & {
    hashtagId: number;
    limit?: number;
    page?: number;
    sortBy?: 'emv' | 'likes' | 'views' | 'comments' | 'shares' | 'date';
  }): Promise<{
    success: boolean;
    message: string;
    data: {
      posts: Array<{
        postId: string;
        platformPostId: string;
        platformId: number;
        platformName: string;
        platformDisplayName: string;
        username: string;
        displayName: string;
        postedAt: string;
        postType: string;
        contentType: string;
        isSponsored: boolean;
        permalink: string;
        caption: string;
        hashtags: Array<{
          hashtag: string;
          position: number;
        }>;
        stats: {
          emv: string;
          likesCount: number;
          viewsCount: number;
          commentsCount: number;
          sharesCount: number;
          savesCount: number;
          playsCount: number;
        };
      }>;
      pagination: {
        currentPage: number;
        totalPages: number;
        totalPosts: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      };
      hashtag: string;
      period: {
        type: string;
        currentStart: string;
        currentEnd: string;
      };
    };
  }> {
    const { customerId, hashtagId, ...queryParams } = params;

    // Handle array parameters properly
    const processedParams: any = { customerId, hashtagId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/hashtag-posts`;
    const searchParams = new URLSearchParams();
    
    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get(endpoint);
  }

  async getEmvOverTime(params: DashboardAnalyticsParams): Promise<{
    success: boolean;
    message: string;
    data: {
      customer: {
        customerId: string;
        customerName: string;
      };
      period: {
        type: string;
        start: string;
        end: string;
      };
      filters: {
        platforms: Array<{
          id: number;
          platformName: string;
          platformDisplayName: string;
        }>;
        accounts: Array<{
          id: string;
          username: string;
          displayName: string;
          platform: {
            id: number;
            platformName: string;
            platformDisplayName: string;
          };
        }>;
      };
      summary: {
        totalEmv: string;
        totalPosts: number;
        averageDailyEmv: string;
        peakDay: {
          date: string;
          emv: string;
          postCount: number;
        };
        totalDays: number;
      };
      dailyData: Array<{
        date: string;
        totalEmv: string;
        totalEmvValue: number;
        postCount: number;
      }>;
    };
  }> {
    const { customerId, ...queryParams } = params;
    
    // Handle array parameters properly
    const processedParams: any = { customerId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/emv-over-time`;
    const searchParams = new URLSearchParams();
    
    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get(endpoint);
  }

  async getPostInsights(params: DashboardAnalyticsParams): Promise<{
    success: boolean;
    message: string;
    data: {
      summary: {
        totalInsights: number;
        totalContentAnalysis: number;
        dateRange: {
          start: string;
          end: string;
          period: string;
        };
      };
      insights: {
        byCategory: {
          performance: Array<{
            id: number;
            type: string;
            category: string;
            title: string;
            description: string;
            priority: 'high' | 'medium' | 'low';
            confidenceLevel: number;
            potentialImpact: 'high' | 'medium' | 'low';
            implementationDifficulty: 'easy' | 'medium' | 'hard';
            estimatedEffortHours: number;
            actionableRecommendations: {
              schedule?: string;
              frequency?: string;
              tools?: string[];
            };
            supportingData: {
              currentAvgEngagement: number;
              peakHourEngagement: number;
              improvementPercentage: number;
            };
            successMetrics: string[];
          }>;
          content: Array<{
            id: number;
            type: string;
            category: string;
            title: string;
            description: string;
            priority: 'high' | 'medium' | 'low';
            confidenceLevel: number;
            potentialImpact: 'high' | 'medium' | 'low';
            implementationDifficulty: 'easy' | 'medium' | 'hard';
            estimatedEffortHours: number;
          }>;
          audience: Array<any>;
          engagement: Array<any>;
        };
        byPriority: {
          high: Array<any>;
          medium: Array<any>;
          low: Array<any>;
        };
        categories: string[];
        priorities: {
          high: number;
          medium: number;
          low: number;
        };
      };
      contentAnalysis: {
        sentiment: {
          positive: number;
          negative: number;
          neutral: number;
          avgScore: number;
          distribution: {
            positive: number;
            negative: number;
            neutral: number;
          };
        };
        topics: {
          top10: Array<{
            topic: string;
            count: number;
          }>;
          totalUnique: number;
        };
        content: {
          categories: {
            top10: Array<{
              category: string;
              count: number;
            }>;
            totalUnique: number;
          };
          quality: {
            avgScore: number;
            distribution: number[];
          };
          readability: {
            avgScore: number;
            distribution: number[];
          };
          brandSafety: {
            avgScore: number;
            distribution: number[];
          };
        };
        engagement: {
          predictions: Array<{
            postType: string;
            predictedLikes: number;
            predictedComments: number;
            predictedShares: number;
            confidence: number;
          }>;
          totalPredictions: number;
        };
      };
    };
  }> {
    const { customerId, ...queryParams } = params;
    
    // Handle array parameters properly
    const processedParams: any = { customerId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/post-insights`;
    const searchParams = new URLSearchParams();
    
    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get(endpoint);
  }

  async getPostInsightsByPostId(postId: number): Promise<{
    success: boolean;
    message: string;
    data: {
      post: {
        postId: number;
        platformPostId: string;
        platform: {
          id: number;
          name: string;
          displayName: string;
        };
        account: {
          id: string;
          username: string;
          displayName: string;
          customer: {
            id: string;
            name: string;
          };
        };
        caption: string;
        postType: string;
        contentType: string;
        postUrl: string;
        postedAt: string;
        isSponsored: boolean;
        isAd: boolean;
        hashtags: string[];
        media: Array<{
          mediaId: number;
          mediaType: string;
          mediaUrl: string;
          displayOrder: number;
          width: number;
          height: number;
          durationSeconds?: number;
        }>;
        stats: {
          likesCount: number;
          commentsCount: number;
          sharesCount: number;
          viewsCount: number;
          savesCount: number;
          emv: number;
          snapshotDate: string;
        };
      };
      insights: {
        total: number;
        items: Array<{
          id: number;
          type: string;
          category: string;
          title: string;
          description: string;
          priority: 'high' | 'medium' | 'low';
          confidenceLevel: number;
          potentialImpact: 'high' | 'medium' | 'low';
          implementationDifficulty: 'low' | 'medium' | 'high';
          estimatedEffortHours: number;
          actionableRecommendations: {
            schedule?: string;
            frequency?: string;
            tools?: string[];
          };
          supportingData: {
            currentAvgEngagement: number;
            peakHourEngagement: number;
            improvementPercentage: number;
          };
          successMetrics: string[];
          benchmarkData: {
            industryAverage: number;
            yourAverage: number;
          };
          trendIndicators: {
            trend: string;
            strength: string;
          };
          seasonalFactors: {
            season: string;
            impact: string;
          };
          createdAt: string;
        }>;
      };
      contentAnalysis: {
        total: number;
        items: Array<{
          id: number;
          analysisType: string;
          analysisModel: string;
          analysisVersion: string;
          sentimentScore: number;
          sentimentLabel: string;
          confidenceScore: number;
          primaryTopics: string[];
          keywords: string[];
          toneAnalysis: {
            enthusiasm: number;
            confidence: number;
            professionalism: number;
          };
          engagementPrediction: {
            predictedLikes: number;
            predictedComments: number;
            predictedShares: number;
            confidence: number;
          };
          contentCategories: string[];
          targetAudience: {
            ageGroup: string;
            interests: string[];
            demographics: string;
          };
          contentQualityScore: number;
          readabilityScore: number;
          emotionalTriggers: string[];
          callToAction: string;
          brandSafetyScore: number;
          complianceFlags: string[];
          processingTimeMs: number;
          createdAt: string;
        }>;
      };
    };
  }> {
    const url = `${this.dashboardBaseUrl}/posts/${postId}/insights`;
    const result = await apiService.get(url);
    return result as any;
  }

  async getEmvByPostType(params: DashboardAnalyticsParams): Promise<{
    success: boolean;
    message: string;
    data: {
      customer: {
        customerId: string;
        customerName: string;
      };
      period: {
        type: string;
        current: {
          start: string;
          end: string;
        };
        previous: {
          start: string;
          end: string;
        };
      };
      filters: {
        platforms: Array<{
          id: number;
          platformName: string;
          platformDisplayName: string;
        }>;
        accounts: Array<{
          id: string;
          username: string;
          displayName: string;
          platform: {
            id: number;
            platformName: string;
            platformDisplayName: string;
          };
        }>;
      };
      summary: {
        totalPostTypes: number;
        current: {
          totalPosts: number;
          totalEmv: string;
          avgEmvPerPost: string;
        };
        previous: {
          totalPosts: number;
          totalEmv: string;
          avgEmvPerPost: string;
        };
        changes: {
          totalPosts: string;
          totalEmv: string;
          avgEmvPerPost: string;
        };
      };
      byPostType: {
        [key: string]: {
          current: {
            count: number;
            totalEmv: string;
            totalEmvValue: number;
            avgEmv: string;
            avgEmvValue: number;
            totalLikes: number;
            totalComments: number;
            totalShares: number;
            totalViews: number;
            avgLikes: number;
            avgComments: number;
            avgShares: number;
            avgViews: number;
          };
          previous: {
            count: number;
            totalEmv: string;
            totalEmvValue: number;
            avgEmv: string;
            avgEmvValue: number;
            totalLikes: number;
            totalComments: number;
            totalShares: number;
            totalViews: number;
            avgLikes: number;
            avgComments: number;
            avgShares: number;
            avgViews: number;
          };
          changes: {
            count: string;
            totalEmv: string;
            avgEmv: string;
            totalLikes: string;
            totalComments: string;
            totalShares: string;
            totalViews: string;
          };
          topPosts: Array<{
            postId: number;
            platformPostId: string;
            caption: string;
            postType: string;
            contentType: string;
            postedAt: string;
            emv: string;
            emvValue: number;
            likes: number;
            comments: number;
            shares: number;
            views: number;
          }>;
        };
      };
      byContentType: {
        [key: string]: {
          count: number;
          totalEmv: string;
          totalEmvValue: number;
          avgEmv: string;
          postTypes: string[];
          percentageOfTotal: number;
        };
      };
      insights: {
        topPerformingPostType: string;
        lowestPerformingPostType: string;
        mostProductivePostType: {
          type: string;
          count: number;
        };
      };
    };
  }> {
    const { customerId, ...queryParams } = params;
    
    // Handle array parameters properly
    const processedParams: any = { customerId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/emv-by-post-type`;
    const searchParams = new URLSearchParams();
    
    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get(endpoint);
  }

  async getEmvByAuthorType(params: DashboardAnalyticsParams): Promise<{
    success: boolean;
    message: string;
    data: {
      breakdown: {
        organic: {
          totalEmv: string;
          postCount: number;
          percentageOfTotal: number;
        };
        collaborator: {
          totalEmv: string;
          postCount: number;
          percentageOfTotal: number;
        };
        totals: {
          totalEmv: string;
          totalPosts: number;
        };
      };
    };
  }> {
    const { customerId, ...queryParams } = params;
    
    // Handle array parameters properly
    const processedParams: any = { customerId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/emv-by-author-type`;
    const searchParams = new URLSearchParams();
    
    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get(endpoint);
  }

  // DEPRECATED: Social Sponsors API Methods - Backend routes removed, now using activation-based sponsorship detection
  /** @deprecated Backend routes removed - use activation-based sponsorship detection instead */
  async getSocialSponsors(params: GetSocialSponsorsParams): Promise<SocialSponsorsResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const endpoint = `/social/sponsors?${queryParams.toString()}`;
    return await apiService.get<SocialSponsorsResponse>(endpoint);
  }

  async getSocialSponsorsList(customerId: string): Promise<SocialSponsorsListResponse> {
    return await apiService.get<SocialSponsorsListResponse>(`/social/sponsors/list?customerId=${encodeURIComponent(customerId)}`);
  }

  async getSocialSponsor(sponsorId: number): Promise<SocialSponsorResponse> {
    return await apiService.get<SocialSponsorResponse>(`/social/sponsors/${sponsorId}`);
  }

  async createSocialSponsor(sponsor: CreateSocialSponsorRequest): Promise<SocialSponsorResponse> {
    return await apiService.post<SocialSponsorResponse>('/social/sponsors', sponsor);
  }

  async updateSocialSponsor(sponsorId: number, updates: UpdateSocialSponsorRequest): Promise<SocialSponsorResponse> {
    return await apiService.put<SocialSponsorResponse>(`/social/sponsors/${sponsorId}`, updates);
  }

  async deleteSocialSponsor(sponsorId: number): Promise<{ success: boolean; message: string }> {
    return await apiService.delete(`/social/sponsors/${sponsorId}`);
  }

  // Social Team Members API Methods
  async getSocialTeamMembers(params: GetSocialTeamMembersParams): Promise<SocialTeamMembersResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const endpoint = `/social/team-members?${queryParams.toString()}`;
    return await apiService.get<SocialTeamMembersResponse>(endpoint);
  }

  async getSocialTeamMember(teamMemberId: number): Promise<SocialTeamMemberResponse> {
    return await apiService.get<SocialTeamMemberResponse>(`/social/team-members/${teamMemberId}`);
  }

  async createSocialTeamMember(teamMember: CreateSocialTeamMemberRequest): Promise<SocialTeamMemberResponse> {
    return await apiService.post<SocialTeamMemberResponse>('/social/team-members', teamMember);
  }

  async updateSocialTeamMember(teamMemberId: number, updates: UpdateSocialTeamMemberRequest): Promise<SocialTeamMemberResponse> {
    return await apiService.put<SocialTeamMemberResponse>(`/social/team-members/${teamMemberId}`, updates);
  }

  async deleteSocialTeamMember(teamMemberId: number): Promise<{ success: boolean; message: string }> {
    return await apiService.delete(`/social/team-members/${teamMemberId}`);
  }

  // DEPRECATED: Social Sponsor Team API methods - Backend routes removed, now using activation-based sponsorship detection
  /** @deprecated Backend routes removed - use activation-based sponsorship detection instead */
  async getSocialSponsorTeam(params: GetSocialSponsorTeamParams): Promise<SocialSponsorTeamResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const endpoint = `/social/sponsor-team?${queryParams.toString()}`;
    return await apiService.get<SocialSponsorTeamResponse>(endpoint);
  }

  async getSocialSponsorTeamById(sponsorTeamId: number): Promise<SocialSponsorTeamSingleResponse> {
    return await apiService.get<SocialSponsorTeamSingleResponse>(`/social/sponsor-team/${sponsorTeamId}`);
  }

  async createSocialSponsorTeam(assignment: CreateSocialSponsorTeamRequest): Promise<SocialSponsorTeamSingleResponse> {
    return await apiService.post<SocialSponsorTeamSingleResponse>('/social/sponsor-team', assignment);
  }

  async updateSocialSponsorTeam(sponsorTeamId: number, updates: UpdateSocialSponsorTeamRequest): Promise<SocialSponsorTeamSingleResponse> {
    return await apiService.put<SocialSponsorTeamSingleResponse>(`/social/sponsor-team/${sponsorTeamId}`, updates);
  }

  async deleteSocialSponsorTeam(sponsorTeamId: number): Promise<{ success: boolean; message: string }> {
    return await apiService.delete(`/social/sponsor-team/${sponsorTeamId}`);
  }

  // DEPRECATED: Social Post Sponsors API methods - Backend routes removed, now using activation-based sponsorship detection
  /** @deprecated Backend routes removed - use activation-based sponsorship detection instead */
  async getPostSponsors(postId: number): Promise<PostSponsorsResponse> {
    return await apiService.get<PostSponsorsResponse>(`/social/post-sponsors/${postId}`);
  }

  async associateSponsorsWithPost(request: AssociateSponsorsRequest): Promise<AssociateSponsorsResponse> {
    return await apiService.post<AssociateSponsorsResponse>('/social/post-sponsors', request);
  }

  async updatePostSponsor(postSponsorId: number, updates: UpdatePostSponsorRequest): Promise<{ success: boolean; message: string; data: PostSponsor }> {
    return await apiService.put<{ success: boolean; message: string; data: PostSponsor }>(`/social/post-sponsors/${postSponsorId}`, updates);
  }

  async disassociateSponsorsFromPost(request: DisassociateSponsorsRequest): Promise<DisassociateSponsorsResponse> {
    return await apiService.delete<DisassociateSponsorsResponse>('/social/post-sponsors', request);
  }

  async getSponsorPosts(sponsorId: number, params?: GetSponsorPostsParams): Promise<SponsorPostsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/social/post-sponsors/sponsor/${sponsorId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return await apiService.get<SponsorPostsResponse>(endpoint);
  }

  // Get brand details
  async getBrandDetails(
    customerId: string,
    brandName: string,
    period: string,
    limit: number = 5,
    customStartDate?: string,
    customEndDate?: string
  ): Promise<{
    success: boolean;
    data: {
      brandDetails: Array<{
        postId: number;
        caption: string;
        postUrl: string;
        postedAt: string;
        platform: {
          id: number;
          name: string;
          displayName: string;
        };
        account: {
          id: string;
          username: string;
          displayName: string;
        };
        metrics: {
          likes: number;
          comments: number;
          shares: number;
          views: number;
          emv: string;
        };
        brandData: {
          brandName: string;
          detectionCount: number;
          totalDurationSeconds: number;
          avgConfidence: number;
        };
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
        hasNext: boolean;
        hasPrev: boolean;
      };
    };
    message?: string;
  }> {
    const params: any = {
      customerId,
      brandName,
      period,
      limit
    };

    // Add custom date range if provided
    if (customStartDate) {
      params.customStartDate = customStartDate;
    }
    if (customEndDate) {
      params.customEndDate = customEndDate;
    }

    try {
      const response = await apiService.get(`${this.dashboardBaseUrl}/post-brand-details`, params);
      return response as any;
    } catch (error) {
      console.error('Error fetching brand details:', error);
      throw error;
    }
  }

  // Update post views
  async updatePostViews(postId: string, newViewsCount: number, originalViewsCount: number): Promise<{ success: boolean; message: string }> {
    return await apiService.patch<{ success: boolean; message: string }>(`/social/dashboard/posts/stats?postId=${postId}`, {
      statType: "views_count",
      overrideValue: newViewsCount,
      originalValue: originalViewsCount,
      reason: "Manual correction for testing"
    });
  }

  // Update post likes
  async updatePostLikes(postId: string, newLikesCount: number, originalLikesCount: number): Promise<{ success: boolean; message: string }> {
    return await apiService.patch<{ success: boolean; message: string }>(`/social/dashboard/posts/stats?postId=${postId}`, {
      statType: "likes_count",
      overrideValue: newLikesCount,
      originalValue: originalLikesCount,
      reason: "Manual correction"
    });
  }

  // Get post media
  // `preferType: 'video'` returns ONLY video rows (or none). Callers that feed the result
  // into a <video> element must pass it — otherwise a thumbnail can rank first and render
  // as a broken player, which is what hid Brand Discovery.
  async getPostMedia(postId: number, partitionId: number, includeBrands?: boolean, preferType?: 'video'): Promise<{
    success: boolean;
    data: {
      media: Array<{
        mediaId: number;
        displayOrder: number;
        mediaType: string;
        mediaUrl: string;
        width: number;
        height: number;
        durationSeconds: string;
        mediaAvailable: boolean;
        media: string;
        contentType: string;
        brands?: Array<{
          brandName: string;
          appearanceCount: number;
          totalDurationSeconds: number;
          detectionCount: number;
          avgConfidence: number;
        }>;
      }>;
    };
  }> {
    const params: any = {
      postId: postId.toString(),
      partitionId: partitionId.toString()
    };

    if (includeBrands) {
      params.includeBrands = 'true';
    }

    if (preferType) {
      params.preferType = preferType;
    }

    return await apiService.get<{
      success: boolean;
      data: {
        media: Array<{
          mediaId: number;
          displayOrder: number;
          mediaType: string;
          mediaUrl: string;
          width: number;
          height: number;
          durationSeconds: string;
          mediaAvailable: boolean;
          media: string;
          contentType: string;
          brands?: Array<{
            brandName: string;
            appearanceCount: number;
            totalDurationSeconds: number;
            detectionCount: number;
            avgConfidence: number;
          }>;
        }>;
      };
    }>(`${this.dashboardBaseUrl}/post-media`, params);
  }

  // Get brand timeline data
  async getBrandTimeline(params: {
    customerId: string;
    brandName: string;
    period: string;
    platformIds?: number[];
    customStartDate?: string;
    customEndDate?: string;
  }): Promise<{
    success: boolean;
    data: {
      brandName: string;
      timeline: Array<{
        date: string;
        detectionCount: number;
        totalDuration: number;
        avgConfidence: number;
        postCount: number;
      }>;
      summary: {
        totalDays: number;
        daysWithData: number;
        totalDetections: number;
        totalDuration: number;
        totalPosts: number;
      };
      filters: any;
    };
    message?: string;
  }> {
    const queryParams: Record<string, string | string[]> = {
      customerId: params.customerId,
      brandName: params.brandName,
      period: params.period
    };

    // Add custom date range parameters when period is 'custom'
    if (params.period === 'custom' && params.customStartDate && params.customEndDate) {
      queryParams.customStartDate = params.customStartDate;
      queryParams.customEndDate = params.customEndDate;
    }

    // Add platform filters if provided
    if (params.platformIds && params.platformIds.length > 0) {
      queryParams.platformIds = params.platformIds.map(id => id.toString());
    }

    return await apiService.get(`${this.dashboardBaseUrl}/brand-timeline`, queryParams);
  }

  // Get brand platform breakdown data
  async getBrandPlatformBreakdown(params: {
    customerId: string;
    brandName: string;
    period: string;
    customStartDate?: string;
    customEndDate?: string;
  }): Promise<{
    success: boolean;
    data: {
      brandName: string;
      platforms: Array<{
        platform: {
          id: number;
          name: string;
          displayName: string;
        };
        uniqueDetections: number;
        percentage: number;
        postCount: number;
        totalDetections: number;
        totalDurationSeconds: number;
        avgConfidence: number;
      }>;
      summary: {
        totalPlatforms: number;
        totalUniqueDetections: number;
        totalPosts: number;
        totalDetections: number;
        totalDurationSeconds: number;
      };
    };
  }> {
    const queryParams: Record<string, string> = {
      customerId: params.customerId,
      brandName: params.brandName,
      period: params.period
    };

    // Add custom date range parameters when period is 'custom'
    if (params.period === 'custom' && params.customStartDate && params.customEndDate) {
      queryParams.customStartDate = params.customStartDate;
      queryParams.customEndDate = params.customEndDate;
    }

    return await apiService.get(`${this.dashboardBaseUrl}/brand-platform-breakdown`, queryParams);
  }

  // Get EMV by date and platform data
  async getEmvByDatePlatform(params: DashboardAnalyticsParams): Promise<{
    success: boolean;
    message: string;
    data: {
      customer: {
        customerId: string;
        customerName: string;
      };
      period: {
        type: string;
        start: string;
        end: string;
      };
      dailyPlatformData: Array<{
        date: string;
        platforms: Array<{
          platformId: number;
          platformName: string;
          platformDisplayName: string;
          totalEmv: string;
          totalEmvValue: number;
          postCount: number;
        }>;
      }>;
    };
  }> {
    const { customerId, ...queryParams } = params;

    // Handle array parameters properly
    const processedParams: any = { customerId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }
    if (params.postTypes) {
      if (Array.isArray(params.postTypes)) {
        delete processedParams.postTypes;
      } else {
        processedParams.postTypes = params.postTypes;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/emv-by-date-platform`;
    const searchParams = new URLSearchParams();

    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }
    if (params.postTypes && Array.isArray(params.postTypes)) {
      params.postTypes.forEach(type => searchParams.append('postTypes', type));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get(endpoint);
  }

  // Get post heatmap data
  async getPostHeatmap(params: DashboardAnalyticsParams): Promise<{
    success: boolean;
    message: string;
    data: {
      customer: {
        customerId: string;
        customerName: string;
      };
      period: {
        type: string;
        start: string;
        end: string;
      };
      filters: {
        platforms: Array<{
          id: number;
          platformName: string;
          platformDisplayName: string;
        }>;
        accounts: Array<{
          id: string;
          username: string;
          displayName: string;
          platform: {
            id: number;
            platformName: string;
            platformDisplayName: string;
          };
        }>;
      };
      postingActivity: {
        weeklyBreakdown: Array<{
          weekNumber: number;
          dayOfWeek: {
            [key: string]: {
              postCount: number;
              intensity: number;
            };
          };
        }>;
        maxPostsPerDay: number;
      };
    };
  }> {
    const { customerId, ...queryParams } = params;

    // Handle array parameters properly
    const processedParams: any = { customerId, ...queryParams };
    if (params.platformIds) {
      if (Array.isArray(params.platformIds)) {
        delete processedParams.platformIds;
      } else {
        processedParams.platformIds = params.platformIds;
      }
    }
    if (params.accountIds) {
      if (Array.isArray(params.accountIds)) {
        delete processedParams.accountIds;
      } else {
        processedParams.accountIds = params.accountIds;
      }
    }
    if (params.postTypes) {
      if (Array.isArray(params.postTypes)) {
        delete processedParams.postTypes;
      } else {
        processedParams.postTypes = params.postTypes;
      }
    }

    let endpoint = `${this.dashboardBaseUrl}/post-heatmap`;
    const searchParams = new URLSearchParams();

    Object.entries(processedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Handle array parameters by appending multiple times
    if (params.platformIds && Array.isArray(params.platformIds)) {
      params.platformIds.forEach(id => searchParams.append('platformIds', id.toString()));
    }
    if (params.accountIds && Array.isArray(params.accountIds)) {
      params.accountIds.forEach(id => searchParams.append('accountIds', id));
    }
    if (params.postTypes && Array.isArray(params.postTypes)) {
      params.postTypes.forEach(type => searchParams.append('postTypes', type));
    }

    if (searchParams.toString()) {
      endpoint += `?${searchParams.toString()}`;
    }

    return await apiService.get(endpoint);
  }
}

export const socialMediaService = new SocialMediaService();